import type { TFunction } from 'i18next';
import { Search, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@shared/components/ui/cn';
import { ROUTE_I18N_KEYS } from '@shared/lib/route-i18n-keys';
import { useUIStore } from '@shared/stores/ui.store';

const PAGE_PATHS = [
  '/dashboard',
  '/reports',
  '/wealth/investors',
  '/wealth/accounts',
  '/transactions',
  '/client-requests',
  '/marketplace',
  '/invoices',
  '/security',
  '/security/users',
  '/security/roles',
  '/security/logs',
  '/security/sessions',
  '/ai-cio',
];

const RECENT_PATHS = ['/dashboard', '/wealth/investors', '/client-requests', '/invoices'];

function labelFor(path: string, t: TFunction): string {
  const key = ROUTE_I18N_KEYS[path];
  return key ? t(key) : path;
}

interface SearchModalProps {
  onNavigate: (path: string) => void;
}

export function SearchModal({ onNavigate }: SearchModalProps) {
  const { t } = useTranslation();
  const { searchOpen, toggleSearch } = useUIStore();
  const inputRef = useRef<HTMLInputElement>(null);
  const activeRef = useRef<HTMLButtonElement>(null);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    const paths = q
      ? PAGE_PATHS.filter((p) => labelFor(p, t).toLowerCase().includes(q))
      : RECENT_PATHS;
    return paths.map((path) => ({ path, label: labelFor(path, t) }));
  }, [query, t]);

  useEffect(() => {
    if (!searchOpen) return;
    setQuery('');
    setActiveIndex(0);
    const id = requestAnimationFrame(() => inputRef.current?.focus());
    return () => {
      cancelAnimationFrame(id);
    };
  }, [searchOpen]);

  useEffect(() => {
    setActiveIndex((i) => (results.length === 0 ? 0 : Math.min(i, results.length - 1)));
  }, [results.length]);

  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  if (!searchOpen) return null;

  const go = (path: string) => {
    onNavigate(path);
    toggleSearch();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex((i) => (results.length ? (i + 1) % results.length : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((i) => (results.length ? (i - 1 + results.length) % results.length : 0));
        break;
      case 'Enter': {
        e.preventDefault();
        const item = results[activeIndex];
        if (item) go(item.path);
        break;
      }
      case 'Escape':
        // Stop the event so the global Escape hotkey doesn't also fire.
        e.preventDefault();
        e.stopPropagation();
        toggleSearch();
        break;
      default:
        break;
    }
  };

  const hasQuery = query.trim().length > 0;

  return (
    <div
      role="presentation"
      className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh]"
      onClick={toggleSearch}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />

      {/* Modal */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions -- the click only stops propagation so a click inside the dialog doesn't hit the backdrop's close handler; it is not a control */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('actions.search')}
        className="relative z-10 w-full max-w-xl overflow-hidden rounded-2xl border bg-popover shadow-2xl"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {/* Input row */}
        <div className="flex items-center gap-3 border-b px-4 py-3">
          <Search className="size-4 shrink-0 text-muted-foreground" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActiveIndex(0);
            }}
            onKeyDown={onKeyDown}
            placeholder={t('search.placeholder', 'Search pages, investors, documents…')}
            aria-label={t('actions.search')}
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
          <button
            type="button"
            onClick={toggleSearch}
            aria-label={t('ui.close')}
            className="rounded-md p-1 text-muted-foreground hover:text-foreground"
          >
            <X className="size-4" aria-hidden="true" />
          </button>
        </div>

        {/* Results */}
        <div className="max-h-[45vh] overflow-y-auto p-2">
          <p className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
            {hasQuery ? t('search.pages', 'Pages') : t('search.recentPages', 'Recent pages')}
          </p>
          {results.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">
              {t('search.noResults', 'No results found')}
            </p>
          ) : (
            results.map((item, i) => (
              <button
                key={item.path}
                ref={i === activeIndex ? activeRef : undefined}
                className={cn(
                  'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm',
                  i === activeIndex
                    ? 'bg-accent text-accent-foreground'
                    : 'hover:bg-accent hover:text-accent-foreground',
                )}
                onMouseEnter={() => {
                  setActiveIndex(i);
                }}
                onClick={() => {
                  go(item.path);
                }}
              >
                <Search className="size-3.5 text-muted-foreground" />
                {item.label}
              </button>
            ))
          )}
        </div>

        {/* Footer hint */}
        <div className="flex items-center gap-4 border-t px-4 py-2.5 text-xs text-muted-foreground">
          <span>
            <kbd className="rounded border px-1 font-mono text-[10px]">↑↓</kbd>{' '}
            {t('search.hints.navigate', 'navigate')}
          </span>
          <span>
            <kbd className="rounded border px-1 font-mono text-[10px]">↵</kbd>{' '}
            {t('search.hints.open', 'open')}
          </span>
          <span>
            <kbd className="rounded border px-1 font-mono text-[10px]">Esc</kbd>{' '}
            {t('search.hints.close', 'close')}
          </span>
        </div>
      </div>
    </div>
  );
}
